import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { useIsMobile } from "../../lib/useIsMobile";
import { Icon } from "../ui/Icon";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import "./CloseShortButton.css";

const CUR_SYMBOL: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
function fmtMoney(n: number, cur: string): string {
  return `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)} ${CUR_SYMBOL[cur] ?? cur}`;
}

/**
 * Miktarı BİRİM BAZINDA yaz: "1.200 koli · 50 kg". Sunucunun `toplam` alanı kalemleri birimlerine
 * bakmadan topluyor (koli + kg tek sayı) — karışık birimli siparişte o sayı anlamsız. Aynı
 * hata sınıfı sipariş süzgeci KPI'ında da yaşandı; burada satırlardan birim birim toplanır.
 */
type SatirMin = { uom: string; taslakta: number; simdiSevk: number; eksikKapanacak: number; rezerve: number };
function birimliToplam(satirlar: SatirMin[], alan: "taslakta" | "simdiSevk" | "eksikKapanacak" | "rezerve"): string {
  const m = new Map<string, number>();
  for (const s of satirlar) {
    const v = s[alan];
    if (v > 0.0001) m.set(s.uom, (m.get(s.uom) ?? 0) + v);
  }
  return [...m].map(([u, v]) => `${fmtQty(v)} ${u}`).join(" · ");
}
/** Sunucunun birim bazlı sonucu ("1.200 koli · 50 kg") — sonuç ekranı bunu kullanır. */
function birimliMetin(b: Array<{ uom: string; miktar: number }>): string {
  return b.map((x) => `${fmtQty(x.miktar)} ${x.uom}`).join(" · ");
}

type KapatmaSonucuT = NonNullable<ReturnType<typeof trpc.order.siparisiKapat.useMutation>["data"]>;

export interface CloseShortOrder {
  id: string;
  code: string;
  status: string;
  lines: Array<{ qty: number; shippedQty: number; reservedQty: number }>;
}

/**
 * "SİPARİŞİ KAPAT — ÜRETİLDİĞİ KADAR SEVK" — buton + önizlemeli onay diyaloğu + mutasyon, TEK yerde.
 *
 * NEDEN AYRI BİLEŞEN: bu eylem İKİ ekranda gerekiyor — Siparişler > Detay ve
 * Sevkiyat > Sevkiyata Hazır listesi (kullanıcı sorunu ORADA görüyor). İki kopya
 * tutulsaydı guard'lar ve uyarı metni zamanla ayrışırdı; bu depoda "aynı işin iki
 * farklı sonucu" daha önce yaşanmış bir hata sınıfı.
 *
 * NE YAPAR (sunucu: api/src/order-close.ts): 1) siparişin açık taslak planları olduğu gibi
 * onaylanır (evrak eksikse idari onay), 2) siparişe bağlı stokta duran üretim için plan
 * otomatik oluşur + onaylanır (stok çıkışı + cari satış borcu), 3) kalan varsa eksik sevkle
 * kapanır. Gerekçe ZORUNLU DEĞİL (kullanıcı kararı, 29 Eyl 2026) — not isteğe bağlı.
 *
 * GÖRÜNÜRLÜK: yalnız Sistem Yöneticisi + onaylı/üretimde/hazır sipariş. Eskiden "en az bir birim
 * sevk edilmiş" şartı vardı; artık YOK — hiç sevk olmamış ama üretimi olan sipariş de kapatılır.
 * Üretim hiç yoksa bunu İSTEMCİ tahmin etmez: sunucu önizlemesi `engel` ile sebebi söyler.
 */
export function CloseShortButton({ order, onClosed }: { order: CloseShortOrder | null | undefined; onClosed?: () => void }) {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  /**
   * YETKİ KONTROLÜ BURADA (paylaşılan bileşende) — iki çağrı noktasını da kapatır.
   * Drawer'ı besleyen `order.get` yalnız görüntüleme yetkisi istiyor; sunucu bu eylemi
   * yalnız Sistem Yöneticisine açıyor (`requireSistemYoneticisi`). Kullanıcıya yapamayacağı
   * YIKICI bir eylem vaat edip sonra FORBIDDEN göstermek kabul edilemez → düğme hiç çıkmaz.
   */
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const yetkili = meQ.data?.user?.sistemYoneticisi === true;
  const [ask, setAsk] = useState(false);
  const [not, setNot] = useState("");
  const notRef = useRef<HTMLTextAreaElement>(null);
  /**
   * Sonuç RAPORU ekranda kalır: kapatma yalnız durumu değiştirmiyor — taslak planları onaylıyor,
   * stoktan mamul çıkarıyor, açık iş emirlerini iptal ediyor ve sevk edilmemiş partileri stoğa
   * bırakıyor. Bunlar sessizce olursa kullanıcı "iş emrim nereye gitti?" diye arar. Bu yüzden
   * diyalog başarıdan sonra ÖZETE dönüşür, kullanıcı "Tamam" deyince kapanır.
   */
  const [sonuc, setSonuc] = useState<KapatmaSonucuT | null>(null);
  const mut = trpc.order.siparisiKapat.useMutation({
    onSuccess: (r) => { setSonuc(r); qc.invalidateQueries(); },
    // Hata = kısmi ilerleme olabilir (sunucu "bu arada … onaylandı" yazar) → her şey tazelensin,
    // önizleme de yeniden hesaplansın; eski önizlemeyle ikinci deneme yapılmasın.
    onError: () => { qc.invalidateQueries(); },
  });

  const gorunur = !!order && yetkili
    && (order.status === "confirmed" || order.status === "in_production" || order.status === "ready");

  /**
   * Önizleme YALNIZ diyalog açıkken çekilir (kalem başına lot taraması yapıyor — pahalı).
   * Mutasyon sürerken / bittikten sonra KAPALI: başarıdaki `invalidateQueries` önizlemeyi
   * yeniden çalıştırıp (sipariş artık 'shipped') sonuç ekranının altında gereksiz istek atmasın.
   * `staleTime: 0` → her açılışta taze hesap (bu arada başka biri plan onaylamış olabilir).
   */
  const onzQ = trpc.order.kapatmaOnizleme.useQuery(
    { id: order?.id ?? "" },
    { enabled: ask && !!order && !sonuc && !mut.isPending && !mut.isSuccess, staleTime: 0, retry: false },
  );
  const onz = onzQ.data;

  type Mod = "sonuc" | "hata" | "yukleniyor" | "engel" | "hazir";
  const mod: Mod = sonuc ? "sonuc" : onzQ.isError ? "hata" : !onz ? "yukleniyor" : onz.engel ? "engel" : "hazir";
  /**
   * ConfirmDialog'un onay düğmesini devre dışı bırakmanın TEK yolu `busy` (iki düğmeyi birden
   * kilitler; ESC + arka plan tıklaması yine kapatır). Yükleme/yeniden hesaplama sırasında
   * kullanılır — eski önizlemeyle onay verilmesin. ENGEL'de ise busy kullanılmaz (Vazgeç kalıcı
   * kilitlenirdi): yıkıcı düğme hiç sunulmaz, tek "Tamam" düğmesi diyaloğu kapatır.
   */
  const busy = mut.isPending || (mod !== "sonuc" && mod !== "hata" && onzQ.isFetching);

  /**
   * Odak: ConfirmDialog onay düğmesine `autoFocus` veriyor ama açılışta düğme `busy` (önizleme
   * yükleniyor) olduğundan odak hiçbir yere gitmiyor — klavye kullanıcısı diyaloğun DIŞINDA
   * kalıyordu. Önizleme hazır olunca odağı not alanına ver (masaüstünde; telefonda klavye
   * açılıp önizlemeyi örtmesin). Açılış başına bir kez.
   */
  const odaklandi = useRef(false);
  useEffect(() => {
    if (!ask) { odaklandi.current = false; return; }
    if (mod !== "hazir" || busy || odaklandi.current || isMobile) return;
    odaklandi.current = true;
    // preventScroll: not alanı en altta; odak diyaloğu oraya kaydırıp özeti (ne olacağını) ekrandan
    // itiyordu — 1280×800'de scrollTop 182 ölçüldü. Kullanıcı önce özeti görmeli.
    const t = setTimeout(() => notRef.current?.focus({ preventScroll: true }), 0);
    return () => clearTimeout(t);
  }, [ask, mod, busy, isMobile]);

  /**
   * SONUÇ EKRANI GÖRÜNEBİLSİN DİYE `sonuc` erken-return'ü DELER.
   * Kapatma başarılı olunca `order.get` tazeleniyor, durum 'shipped' oluyor ve
   * `gorunur` false'a düşüyor — bileşen (dolayısıyla diyalog) anında unmount
   * oluyordu; kullanıcı ne iptal edilen iş emrini ne stoğa dönen partiyi
   * görebiliyordu. Ölçülerek bulundu.
   */
  if (!sonuc && (!gorunur || !order)) return null;

  const kapat = () => {
    // İşlem sürerken ESC/arka plan diyaloğu kapatmasın: sonuç raporu görünmez bir durumda
    // kalır ve kullanıcı ne olduğunu hiç görmezdi.
    if (mut.isPending) return;
    const bitti = !!sonuc;
    setAsk(false);
    setNot("");
    setSonuc(null);
    mut.reset();
    if (bitti) onClosed?.();
  };

  const onayla = () => {
    if (mod === "sonuc" || mod === "engel") { kapat(); return; }
    if (mod === "hata") { void onzQ.refetch(); return; }
    if (mod !== "hazir" || !order || busy) return;
    const n = not.trim();
    mut.mutate(n ? { id: order.id, not: n } : { id: order.id });
  };

  const mutHata = mut.error ? (
    <div className="form-banner-err" role="alert">
      <Icon name="alertTriangle" size={15} />
      <span className="csb-banner-metin">{mut.error.message}</span>
    </div>
  ) : null;

  let icerik: ReactNode;
  if (mod === "sonuc" && sonuc) {
    icerik = (
      <div className="csb">
        <div className="form-banner-ok" role="status">
          <Icon name="checkCircle" size={15} />
          <span className="csb-banner-metin">
            <b className="mono">{sonuc.code}</b> kapatıldı —{" "}
            {sonuc.eksikBirimli.length
              ? <>kalan <b className="mono">{birimliMetin(sonuc.eksikBirimli)}</b> sevk edilmeyecek (eksik sevk).</>
              : <>tamamı sevk edildi.</>}
          </span>
        </div>
        <ul className="csb-sonuc-liste">
          {sonuc.onaylananTaslaklar.length ? (
            <li>
              <b>{sonuc.onaylananTaslaklar.length}</b> taslak plan onaylandı:{" "}
              {sonuc.onaylananTaslaklar.map((t, i) => (
                <span key={t.code}>
                  {i ? ", " : ""}<span className="mono">{t.code}</span>
                  {t.idari ? <span className="csb-idari"> (idari — evrak eksik)</span> : null}
                </span>
              ))}
            </li>
          ) : null}
          {sonuc.hedefeEklenen ? (
            <li>
              Kalan partiler <b className="mono">{sonuc.hedefeEklenen.code}</b> planına eklendi: <b className="mono">{birimliMetin(sonuc.hedefeEklenen.birimli)}</b>
              {sonuc.haricTutulan.length ? " — plan, yüklenmez işaretli partiler dışında sevk edildi." : " — plan tüm partilerle sevk edildi."}
            </li>
          ) : null}
          {sonuc.yeniSevkiyat ? (
            <li>Yeni sevkiyat oluşturulup onaylandı: <b className="mono">{sonuc.yeniSevkiyat.code}</b> · <b className="mono">{birimliMetin(sonuc.yeniSevkiyat.birimli)}</b> stoktan çıktı.</li>
          ) : null}
          {sonuc.haricTutulan.length ? (
            <li>
              Yüklenmez işaretli olduğu için sevk edilmedi:{" "}
              {sonuc.haricTutulan.map((h, i) => (
                <span key={`${h.taslakCode}-${h.lotCode}`}>{i ? ", " : ""}<span className="mono">{h.lotCode}</span> ({fmtQty(h.miktar)} {h.uom})</span>
              ))}
            </li>
          ) : null}
          {sonuc.serbestRezervasyonBirimli.length ? <li><b className="mono">{birimliMetin(sonuc.serbestRezervasyonBirimli)}</b> rezervasyon serbest bırakıldı.</li> : null}
          {sonuc.iptalIsEmirleri.length ? <li><b>{sonuc.iptalIsEmirleri.length}</b> iş emri iptal edildi: <span className="mono">{sonuc.iptalIsEmirleri.join(", ")}</span></li> : null}
          {sonuc.serbestPartiler.length ? <li><b>{sonuc.serbestPartiler.length}</b> parti serbest stoğa bırakıldı (artık başka siparişe atanabilir).</li> : null}
        </ul>
        {sonuc.korunanBaslamisIsEmirleri.length ? (
          <div className="form-banner-warn" role="alert">
            <Icon name="alertTriangle" size={15} />
            <span className="csb-banner-metin">
              <b>Başlamış iş emri iptal edilmedi</b> (hammadde zaten tüketilmiş):{" "}
              <span className="mono">{sonuc.korunanBaslamisIsEmirleri.join(", ")}</span>. Üretim ekranından elle karara bağlayın.
            </span>
          </div>
        ) : null}
      </div>
    );
  } else if (mod === "hata") {
    icerik = (
      <div className="csb">
        {mutHata}
        <div className="form-banner-err" role="alert">
          <Icon name="alertTriangle" size={15} />
          <span className="csb-banner-metin">Önizleme alınamadı: {onzQ.error?.message}</span>
        </div>
      </div>
    );
  } else if (mod === "yukleniyor" || !onz) {
    icerik = (
      <div className="csb">
        {mutHata}
        <div className="csb-yukleniyor" role="status">
          <Icon name="refresh" size={15} />
          <span>Önizleme hesaplanıyor — taslak planlar ve stoktaki üretim kontrol ediliyor…</span>
        </div>
      </div>
    );
  } else if (mod === "engel") {
    icerik = (
      <div className="csb">
        {mutHata}
        <div className="form-banner-err" role="alert">
          <Icon name="alertTriangle" size={15} />
          <span className="csb-banner-metin"><b className="mono">{onz.code}</b> kapatılamaz: {onz.engel}</span>
        </div>
      </div>
    );
  } else {
    const taslakSayi = onz.taslaklar.length;
    const idariSayi = onz.taslaklar.filter((t) => t.eksikEvrak.length > 0).length;
    const simdi = birimliToplam(onz.satirlar, "simdiSevk");
    const eksik = birimliToplam(onz.satirlar, "eksikKapanacak");
    const tamamSevk = onz.toplam.eksikKapanacak <= 0.0001;
    const hedefIrsaliyeli = onz.taslaklar.find((t) => t.hedef && t.irsaliyeNo) ?? null;
    icerik = (
      <div className="csb">
        {mutHata}
        <p className="csb-giris">
          <b className="mono">{onz.code}</b> siparişi üretildiği kadar sevk edilip <b>Sevk Edildi</b> olarak kapatılacak.
        </p>

        <ul className="csb-adimlar">
          {taslakSayi ? (
            <li className={`csb-adim${idariSayi ? " warn" : " info"}`}>
              <Icon name="fileText" size={15} />
              <span className="csb-adim-metin">
                <b>{taslakSayi}</b> taslak plan onaylanacak
                {idariSayi ? <span className="csb-idari"> ({idariSayi} tanesi evraksız — idari onay)</span> : null}
                <span className="csb-alt">
                  {onz.taslaklar.map((t, i) => (
                    <span key={t.id}>
                      {i ? " · " : ""}<span className="mono">{t.code}</span>
                      {t.hedef ? <span className="csb-hedef"> (kalan partiler buna eklenecek)</span> : null}
                      {t.eksikEvrak.length ? ` (eksik: ${t.eksikEvrak.join(", ")})` : ""}
                    </span>
                  ))}
                </span>
              </span>
            </li>
          ) : null}
          {/* Onay, bakiyesi yetmeyen taslak satırını bakiyeye kırpar / taşınan lotun yerini düzeltir.
              Önizleme miktarları bunu zaten içeriyor — ama NEDEN farklı olduğu da söylenmeli. */}
          {onz.taslaklar.some((t) => t.duzeltme.length) ? (
            <li className="csb-adim warn">
              <Icon name="alertTriangle" size={15} />
              <span className="csb-adim-metin">
                Taslak planlarda onayda otomatik düzeltilecek satırlar var
                <span className="csb-alt">
                  {onz.taslaklar.filter((t) => t.duzeltme.length).map((t) => (
                    <span key={t.id} className="csb-duzeltme"><span className="mono">{t.code}</span> — {t.duzeltme.join(" · ")}</span>
                  ))}
                </span>
              </span>
            </li>
          ) : null}
          <li className={`csb-adim${simdi ? " info" : ""}`}>
            <Icon name="truck" size={15} />
            <span className="csb-adim-metin">
              {onz.hedefTaslak
                ? <>Kalan partiler <span className="mono">{onz.hedefTaslak.code}</span> planına eklenecek: <b className="mono">{simdi || "0"}</b></>
                : <>Şimdi sevk edilecek (stoktan çıkış): <b className="mono">{simdi || "0"}</b></>}
              {simdi ? (
                <span className="csb-alt">
                  {/* Kullanıcı kararı (2 Eki 2026): mevcut plan varsa ayrı sevkiyat AÇILMAZ — tüm partiler tek planda çıkar. */}
                  {onz.hedefTaslak ? "Ayrı sevkiyat açılmaz; plan tüm partilerle birlikte onaylanacak." : "Sevkiyat planı otomatik oluşturulup onaylanacak."}
                </span>
              ) : null}
            </span>
          </li>
          {/* Sessizlik yasağı: stokta duran parti neden eklenmiyor — kullanıcı "mal yok" sanmasın. */}
          {onz.haricTutulan.length ? (
            <li className="csb-adim warn">
              <Icon name="alertTriangle" size={15} />
              <span className="csb-adim-metin">
                Yüklenmez işaretli olduğu için EKLENMEYECEK partiler (stokta kalır):
                <span className="csb-alt">
                  {onz.haricTutulan.map((h) => (
                    <span key={`${h.taslakCode}-${h.lotCode}`} className="csb-duzeltme">
                      <span className="mono">{h.lotCode}</span> · <span className="mono">{fmtQty(h.miktar)} {h.uom}</span> ({h.taslakCode}{h.not ? ` — ${h.not}` : ""})
                    </span>
                  ))}
                </span>
              </span>
            </li>
          ) : null}
          {hedefIrsaliyeli && simdi ? (
            <li className="csb-adim warn">
              <Icon name="fileText" size={15} />
              <span className="csb-adim-metin">
                <span className="mono">{hedefIrsaliyeli.code}</span> planının irsaliyesi (<span className="mono">{hedefIrsaliyeli.irsaliyeNo}</span>) zaten kesilmiş
                <span className="csb-alt">Eklenecek partiler bu irsaliyede ve kantar kaydında yer almaz — gerekirse belgeyi güncelleyin. Plana not düşülür.</span>
              </span>
            </li>
          ) : null}
          {tamamSevk ? (
            <li className="csb-adim ok">
              <Icon name="checkCircle" size={15} />
              <span className="csb-adim-metin">Tamamı sevk edilecek — eksik kalan yok.</span>
            </li>
          ) : (
            <li className="csb-adim warn">
              <Icon name="alertTriangle" size={15} />
              <span className="csb-adim-metin">
                Eksik sevkle kapanacak: <b className="mono">{eksik}</b>
                <span className="csb-alt">Bu miktar bir daha sevk edilmeyecek sayılır.</span>
              </span>
            </li>
          )}
          {onz.acikIsEmri > 0 ? (
            <li className="csb-adim warn">
              <Icon name="factory" size={15} />
              <span className="csb-adim-metin"><b className="mono">{onz.acikIsEmri}</b> açık iş emri iptal edilecek (başlamış olanlara dokunulmaz).</span>
            </li>
          ) : null}
          {onz.rezervasyon > 0.0001 ? (
            <li className="csb-adim">
              <Icon name="lock" size={15} />
              <span className="csb-adim-metin"><b className="mono">{birimliToplam(onz.satirlar, "rezerve")}</b> rezervasyon serbest bırakılacak.</span>
            </li>
          ) : null}
          <li className="csb-adim">
            <Icon name="boxes" size={15} />
            <span className="csb-adim-metin">Sevk edilmemiş fazla üretim serbest stoğa bırakılacak.</span>
          </li>
        </ul>

        <h4 className="csb-bolum-baslik">Kalemler</h4>
        <ul className="csb-kalemler">
          {onz.satirlar.map((s) => (
            <li key={s.orderLineId} className="csb-kalem">
              <div className="csb-kalem-bas">
                <span className="csb-kalem-ad">{s.itemName}</span>
                <span className="csb-kod mono">{s.itemCode}</span>
              </div>
              <dl className="csb-kv">
                <div><dt>Sipariş</dt><dd className="mono">{fmtQty(s.siparis)} {s.uom}</dd></div>
                <div><dt>Sevkli</dt><dd className={`mono${s.sevkli > 0.0001 ? "" : " sifir"}`}>{fmtQty(s.sevkli)} {s.uom}</dd></div>
                <div><dt>Taslakta</dt><dd className={`mono${s.taslakta > 0.0001 ? "" : " sifir"}`}>{fmtQty(s.taslakta)} {s.uom}</dd></div>
                <div><dt>Şimdi</dt><dd className={`mono${s.simdiSevk > 0.0001 ? " simdi" : " sifir"}`}>{fmtQty(s.simdiSevk)} {s.uom}</dd></div>
                <div><dt>Eksik</dt><dd className={`mono${s.eksikKapanacak > 0.0001 ? " eksik" : " sifir"}`}>{fmtQty(s.eksikKapanacak)} {s.uom}</dd></div>
              </dl>
            </li>
          ))}
        </ul>

        {/* Cari — PARA BİRİMİ BAZINDA ayrı ayrı; TL'ye çevrilip tek rakama indirgenmez (CLAUDE.md ❌#6). */}
        {onz.cari.length ? (
          <div className="csb-cari">
            <span className="csb-cari-lbl">Cariye yazılacak:</span>
            {onz.cari.map((c, i) => (
              <span key={c.paraBirimi}>
                {i ? <span className="csb-cari-ayrac" aria-hidden="true">· </span> : null}
                <span className="csb-cari-tutar mono">{fmtMoney(c.tutar, c.paraBirimi)}</span>
              </span>
            ))}
          </div>
        ) : null}
        {onz.cariKartiYok ? (
          <div className="form-banner-warn" role="note">
            <Icon name="alertTriangle" size={15} />
            <span className="csb-banner-metin">
              Müşterinin <b>cari kartı yok</b> — bu kapatma cariye borç yazmayacak (stok çıkışı yine yapılır).
            </span>
          </div>
        ) : null}
        {onz.fiyatsizSatir > 0 ? (
          <div className="form-banner-warn" role="note">
            <Icon name="alertTriangle" size={15} />
            <span className="csb-banner-metin">
              <b className="mono">{onz.fiyatsizSatir}</b> kalemin fiyatı yok — bu kalemler için cariye tutar yazılmayacak.
            </span>
          </div>
        ) : null}

        <div className="form-field">
          <label className="lbl" htmlFor="cs-not">Not (isteğe bağlı)</label>
          <textarea
            id="cs-not"
            ref={notRef}
            className="form-textarea"
            rows={2}
            maxLength={500}
            value={not}
            onChange={(e) => setNot(e.target.value)}
            placeholder="Örn. Müşteri kalan miktardan vazgeçti · Üretim tutmadı"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      {gorunur && order ? (
        <button
          type="button"
          className="btn-md danger-outline"
          onClick={() => { mut.reset(); setSonuc(null); setAsk(true); }}
          disabled={mut.isPending}
          title="Üretilen kadarını sevk et (plan otomatik oluşur), kalanı eksik sevkle kapat"
        >
          <Icon name="checkCircle" size={15} />{mut.isPending ? "Kapatılıyor…" : "Siparişi Kapat"}
        </button>
      ) : null}

      <ConfirmDialog
        open={ask}
        busy={busy}
        title={mod === "sonuc" ? "Sipariş kapatıldı" : mod === "engel" ? "Sipariş kapatılamaz" : "Siparişi kapat — üretildiği kadar sevk"}
        tone={mod === "hazir" || mod === "yukleniyor" ? "danger" : "primary"}
        confirmLabel={mod === "sonuc" || mod === "engel" ? "Tamam" : mod === "hata" ? "Tekrar Dene" : "Siparişi Kapat"}
        // Boş etiket → düğme CSS'te gizlenir (CloseShortButton.css `:empty`) — tek "Tamam" kalır.
        cancelLabel={mod === "sonuc" || mod === "engel" ? "" : "Vazgeç"}
        onCancel={kapat}
        onConfirm={onayla}
        message={icerik}
      />
    </>
  );
}
